import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, LockOpen, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { isAccountsHead } from "@/components/GlobalSwitcher";
import { decideCorrection, listAccountsCorrections, listMonthLocks, unlockMonth } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";
import { numberLocale } from "@/lib/money";

// Locked months (closed after reconciliation) and the gyms' correction requests against them.
export default function MonthClose({ gyms, gym }) {
  const { user } = useAuth();
  const head = isAccountsHead(user);
  const [locks, setLocks] = useState(null);
  const [corr, setCorr] = useState([]);
  const load = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([listMonthLocks(), listAccountsCorrections()]);
      setLocks(l);
      setCorr(c);
    } catch (e) { toast.error(errMsg(e, "Could not load")); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const name = Object.fromEntries((gyms || []).map((g) => [g.id, g.name]));

  const unlock = async (l) => {
    const reason = window.prompt(`Reopen ${name[l.facility_id] || l.facility_id} ${l.month}? Why?`);
    if (!reason) return;
    try { await unlockMonth({ facility_id: l.facility_id, month: l.month, reason }); toast.success("Month reopened (logged)"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not unlock")); }
  };
  const decide = async (c, approve) => {
    const note = approve ? "" : window.prompt("Why is it rejected?");
    if (!approve && !note) return;
    try { await decideCorrection(c.id, { approve, note }); toast.success(approve ? "Approved: the record can be changed for 24 hours" : "Rejected"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); }
  };

  if (!locks) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const lockRows = locks.filter((l) => !gym || l.facility_id === gym);
  const corrRows = corr.filter((c) => !gym || c.facility_id === gym);
  return (
    <div className="space-y-5" data-testid="accounts-close">
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-zinc-100">Correction requests</div>
          <div className="text-[11px] text-zinc-500">A gym asks to change a record in a closed month. Approving opens that record (or the whole month, for a missing record) for 24 hours. Everything is logged.</div>
        </div>
        {corrRows.length === 0 ? <Empty>No correction requests.</Empty> : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Requested</th><th className="px-3 py-2 font-medium">Gym · month</th>
              <th className="px-3 py-2 font-medium">Record</th><th className="px-3 py-2 font-medium">What / why</th><th className="px-3 py-2 font-medium">Status</th><th /></tr></thead>
            <tbody>
              {corrRows.map((c) => (
                <tr key={c.id} className={TR}>
                  <td className="px-3 py-2 text-xs text-zinc-400">{String(c.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">{c.requested_by}</div></td>
                  <td className="px-3 py-2 text-zinc-300">{c.gym}<div className="text-[10px] text-zinc-500">{c.month}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{c.member || c.record_type}</td>
                  <td className="px-3 py-2 text-xs"><div className="text-zinc-200">{c.what}</div><div className="text-zinc-500">{c.why}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{c.status}{c.open_until && <div className="text-[10px] text-zinc-500">open until {c.open_until.slice(0, 16).replace("T", " ")}</div>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {c.status === "requested" && <>
                      <Button size="sm" variant="ghost" onClick={() => decide(c, true)} className="h-7 text-emerald-300"><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => decide(c, false)} className="h-7 text-rose-300"><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-zinc-100">Closed months</div>
          <div className="text-[11px] text-zinc-500">Locked from Bank reconciliation once the tally reaches 0. Sales, payments, refunds, expenses and capex dated in a closed month can't be changed. {head ? "You can reopen one with a reason." : "Only the head of accounts can reopen one."}</div>
        </div>
        {lockRows.length === 0 ? <Empty>No months closed yet.</Empty> : (
          <table className="w-full text-sm">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Month</th><th className="px-3 py-2 font-medium">Closed by</th><th className="px-3 py-2 font-medium">Note</th><th /></tr></thead>
            <tbody>
              {lockRows.map((l) => (
                <tr key={`${l.facility_id}:${l.month}`} className={TR}>
                  <td className="px-3 py-2 text-zinc-300">{name[l.facility_id] || l.facility_id}</td>
                  <td className="px-3 py-2 tabular-nums text-zinc-300">{l.month}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{l.locked_by}<div className="text-[10px] text-zinc-500">{String(l.locked_at || "").slice(0, 10)}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{l.forced && <div className="text-amber-300">Closed without reconciling · {l.unexplained_at_close?.toLocaleString(numberLocale())} unexplained</div>}{l.note}</td>
                  <td className="px-3 py-2 text-right">{head && <Button size="sm" variant="ghost" onClick={() => unlock(l)} className="h-7"><LockOpen className="h-3.5 w-3.5 mr-1" /> Reopen</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
